import React, { useEffect, useState, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Toast,
  ToastContainer,
  Spinner,
} from "react-bootstrap";
import img from "../../assets/svg/report.svg";
import DatePicker from "react-datepicker";
import {
  downloadBase64File,
  getCallReportApi,
  getQuickReportApi,
  exportQuickReportApi,
  getReportFieldsApi,
} from "./report-api";
import "./report.css";
import CommonDropdown from "../common/common_dropDown";
import { FaRegCalendarAlt } from "react-icons/fa";
import CommonStaticDropdown from "../common/common_static_dropdown";
import { sanitizeInput } from "../common/security-utils";
import AsyncSelect from "react-select/async";
import { apiCallGeneric } from "../service/api-service";

const multiselectCustomStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #E5E5E5",
    padding: "2px",
    fontWeight: "500",
    fontSize: "13px",
    color: "#212129",
    minHeight: "38px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#6929E2"
      : state.isFocused
        ? "#f0ebfa"
        : "#fff",
    color: state.isSelected ? "#fff" : "#212129",
    padding: 10,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#F6F7F8",
    color: "#212129",
    borderRadius: "5px",
    padding: "2px 5px",
  }),
  multiValueLabel: (provided) => ({ ...provided, color: "#212129" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#212129",
    cursor: "pointer",
    ":hover": { backgroundColor: "red", color: "white" },
  }),
};

const ReportPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;
  const [loading, setLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState("QuickReport");
  const [quickReportType, setQuickReportType] = useState({
    value: "lead",
    label: "Lead",
  });
  const [customReportType, setCustomReportType] = useState({
    value: "Lead",
    label: "Lead",
  });

  const [fieldsList, setFieldsList] = useState([]);
  const [filters, setFilters] = useState({});

  const reportTypes = [
    { value: "Lead", label: "Lead" },
    { value: "Call", label: "Call" },
    { value: "customer_report", label: "Customer Report" },
    { value: "item_report", label: "Item Report" },
    { value: "order_report", label: "Order Report" },
    { value: "invoice_report", label: "Invoice Report" },
    { value: "payment_report", label: "Payment Report" },
    { value: "outstanding_report", label: "Outstanding Report" },
    { value: "salesperson_ordersummary_report", label: "Sales Person Order Summary" },
    { value: "location_ordersummary_report", label: "Location Order Summary" },
    { value: "credit_note_report", label: "Credit Note Report" },
    { value: "sale_person_summary_report", label: "Sale Person Summary" },
    { value: "sale_person_overview_report", label: "Sale Person Overview" },
    { value: "gst_report", label: "GST Report" },
    { value: "tds_report", label: "TDS Report" },
    { value: "task_report", label: "Task Report" },
    { value: "invoice_bill_report", label: "Invoice Bill Report" },
    { value: "charges_report", label: "Charges Report" },
  ];

  const [callType, setCallType] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [quickReportList, setQuickReportList] = useState([]);
  const [quickDateRange, setQuickDateRange] = useState([null, null]);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [quick_from_date, quick_to_date] = quickDateRange;
  const [custom_from_date, custom_to_date] = customDateRange;
  const [leadOwnerId, setLeadOwnerId] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null);
  const [cityId, setCityId] = useState(null);
  const [stateId, setStateId] = useState(null);
  const [countryId, setCountryId] = useState(null);
  const [purpose, setPurpose] = useState(null);

  // VAPT Security: Date validation
  const isValidDate = (date) => {
    if (!date) return true; // null dates are allowed
    return date instanceof Date && !isNaN(date.getTime());
  };

  // VAPT Security: Sanitize array data from API
  const sanitizeArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => {
      if (typeof item === "object" && item !== null) {
        return sanitizeObject(item);
      }
      return item;
    });
  };

  const sanitizeObject = (obj) => {
    if (!obj || typeof obj !== "object") return {};
    const sanitized = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === "string") {
        sanitized[key] = sanitizeInput(value);
      } else if (Array.isArray(value)) {
        sanitized[key] = sanitizeArray(value);
      } else {
        sanitized[key] = value;
      }
    });
    return sanitized;
  };

  // Reusable function to show snackbar notifications
  const showNotification = (message, isSuccess = true) => {
    setSnackbarMessage(message);
    setSnackbarStatus(isSuccess);
    setShowSnackbar(true);
  };

  // Reusable function to validate date range
  const validateDateRange = (fromDate, toDate) => {
    if (
      (fromDate && !isValidDate(fromDate)) ||
      (toDate && !isValidDate(toDate))
    ) {
      showNotification("Invalid date format", false);
      return false;
    }
    return true;
  };

  // Reusable function to handle report download
  const handleReportDownload = (
    base64String,
    fileName,
    successMessage = "Report downloaded successfully",
  ) => {
    downloadBase64File(base64String, fileName);
    showNotification(successMessage, true);
  };

  //RESET
  const handleReset = () => {
    setLeadOwnerId(null);
    setLeadStatus(null);
    setCallType(null);
    setCityId(null);
    setStateId(null);
    setCountryId(null);
    setPurpose(null);
    setQuickDateRange([null, null]);
    setCustomDateRange([null, null]);
    setFilters({});
  };

  const getAsyncUrl = (fieldName) => {
    switch (fieldName) {
      case "lead_owner_id":
      case "sales_person_id":
        return `${apiUrl}get_lead_owner`;
      case "customer_id":
        return `${apiUrl}get_customers`;
      case "location_id":
        return `${adminUrl}get_location`;
      case "lead_id":
        return `${apiUrl}get_leads`;
      case "city_id":
        return `${adminUrl}get_city`;
      case "state_id":
        return `${adminUrl}get_state`;
      case "country_id":
        return `${adminUrl}get_country`;
      case "call_purpose":
        return `${apiUrl}get_call_purpose`;
      default:
        return "";
    }
  };

  const loadAsyncOptions = async (inputValue, fieldName) => {
    try {
      const url = getAsyncUrl(fieldName);
      if (!url) return [];
      const response = await apiCallGeneric("GET", url, null, {
        params: { term: inputValue },
      });
      if (response && response.status === 200 && response.data?.success) {
        return response.data.suggestionlist || [];
      }
    } catch (err) {
      console.error("Error loading async options:", err);
    }
    return [];
  };

  const fetchReportFields = useCallback(async (reportType) => {
    try {
      const response = await getReportFieldsApi(reportType);
      if (response && response.success === true) {
        setFieldsList(response.data || []);
      } else {
        setFieldsList([]);
      }
    } catch (err) {
      setFieldsList([]);
    }
  }, []);

  useEffect(() => {
    if (customReportType?.value) {
      fetchReportFields(customReportType.value);
      setFilters({});
      setCustomDateRange([null, null]);
    }
  }, [customReportType?.value, fetchReportFields]);
  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };
  //GET QUICK REPORT
  const getQuickReport = useCallback(async (type = "lead") => {
    try {
      // VAPT Security: Sanitize type parameter
      const sanitizedType = sanitizeInput(type);
      const response = await getQuickReportApi(sanitizedType);
      if (response && response.success === true) {
        // VAPT Security: Sanitize API response data
        const sanitizedData = sanitizeArray(response.data || []);
        setQuickReportList(sanitizedData);
      }
    } catch (error) {
      // Error handled internally
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getQuickReport(quickReportType.value);
  }, [quickReportType.value, getQuickReport]);
  //EXPORT QUICK
  const exportQuickGet = async (item) => {
    setQuickLoading(true);
    setLoadingItemId(item.value);
    try {
      // VAPT Security: Validate dates before API call
      if (!validateDateRange(quick_from_date, quick_to_date)) {
        setQuickLoading(false);
        setLoadingItemId(null);
        return;
      }

      const response = await exportQuickReportApi(
        item.value,
        quick_from_date,
        quick_to_date,
      );

      if (response?.data?.success) {
        const base64String = response.data.base64;
        // VAPT Security: Sanitize filename
        const fileName =
          sanitizeInput(response.data.file_name) ||
          `Report_${new Date().getTime()}.xlsx`;
        const message =
          sanitizeInput(response.data.message) ||
          "Report downloaded successfully";

        handleReportDownload(base64String, fileName, message);
      } else {
        // VAPT Security: Sanitize error message
        const errorMessage =
          sanitizeInput(response.data?.message) || "Failed to download report";
        showNotification(errorMessage, false);
      }
    } catch (error) {
      // Error handled internally
      showNotification("Failed to download report", false);
    } finally {
      setQuickLoading(false);
      setLoadingItemId(null);
    }
  };

  function formatDateToYMD(date) {
    // VAPT Security: Validate date object
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
  const handleCustomExport = async () => {
    setLoading(true);
    try {
      // VAPT Security: Validate dates before processing
      if (!validateDateRange(custom_from_date, custom_to_date)) {
        setLoading(false);
        return;
      }

      // Check validation for required fields (e.g. sheets)
      const requiredField = fieldsList.find(f => f.required);
      if (requiredField && (!filters[requiredField.field] || filters[requiredField.field].length === 0)) {
        showNotification(`Please select at least one option for ${requiredField.label}`, false);
        setLoading(false);
        return;
      }

      let params = {};
      fieldsList.forEach((field) => {
        const fieldName = field.field;
        const val = filters[fieldName];

        if (fieldName === "fromDate") {
          params.fromDate = custom_from_date ? formatDateToYMD(custom_from_date) : "";
        } else if (fieldName === "toDate") {
          params.toDate = custom_to_date ? formatDateToYMD(custom_to_date) : "";
        } else if (fieldName === "from_date") {
          params.from_date = custom_from_date ? formatDateToYMD(custom_from_date) : "";
        } else if (fieldName === "to_date") {
          params.to_date = custom_to_date ? formatDateToYMD(custom_to_date) : "";
        } else if (fieldName === "invoice_id" && typeof val === "string") {
          params.invoice_id = val.split(',').map(s => s.trim()).filter(Boolean);
        } else if (field.type === "multiselect_async") {
          if (Array.isArray(val)) {
            params[fieldName] = val.map(v => (v && v.value !== undefined ? v.value : v)).filter(Boolean);
          } else if (val && val.value !== undefined) {
            params[fieldName] = [val.value];
          } else {
            params[fieldName] = [];
          }
        } else if (field.type === "select_async") {
          params[fieldName] = val && val.value !== undefined ? val.value : (val ?? "");
        } else {
          params[fieldName] = val !== undefined ? val : "";
        }
      });

      // Special cases for pre-existing reports that don't match the new naming
      let apiEndpoint = customReportType.value;
      if (apiEndpoint === "Lead") {
        apiEndpoint = "leads";
      } else if (apiEndpoint === "Call") {
        apiEndpoint = "calls";
      }

      const response = await getCallReportApi(params, apiEndpoint);
      if (response?.success === true) {
        const base64String = response?.base64;
        const typeSlug = customReportType?.value;

        // VAPT Security: Sanitize filename
        const fileName =
          sanitizeInput(response?.file_name) ||
          `Reports_${typeSlug}_${new Date().getTime()}.xlsx`;
        const message =
          sanitizeInput(response?.message) || "Report downloaded successfully";

        handleReportDownload(base64String, fileName, message);
      } else {
        // VAPT Security: Sanitize error message
        const errorMessage =
          sanitizeInput(response?.message) || "Failed to generate report";
        showNotification(errorMessage, false);
      }
    } catch (error) {
      showNotification("Failed to generate report", false);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Container fluid style={{ padding: "0px" }}>
      {" "}
      <div className="d-flex">
        <div className="rounded-4 flex-grow-1 bg-half-white">
          <div style={{ marginBottom: "32px" }}>
            <div
              style={{
                borderRadius: "16px",
                padding: "32px",
                gap: "24px",
                backgroundColor: "#FFFFFF",
                margin: "16px",
              }}
            >
              <Row className="align-items-center">
                <Col sm={12} md={8}>
                  <div style={{ marginTop: "16px" }}>
                    <div style={{ height: "18px" }}>
                      <span
                        style={{
                          fontWeight: "700",
                          fontSize: "24px",
                          lineHeight: "22px",
                          color: "#212129",
                        }}
                      >
                        {selectedReportType === "QuickReport"
                          ? "Quick Report Download"
                          : "Customize your export"}
                      </span>
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <span
                        style={{
                          fontWeight: "400",
                          fontSize: "14px",
                          lineHeight: "22px",
                          color: "#8790A1",
                        }}
                      >
                        {selectedReportType === "QuickReport" ? (
                          <>
                            Quickly create and save comprehensive reports in
                            just moments. One click is all it takes to access
                            the information you need, whenever you need it!
                          </>
                        ) : (
                          <>
                            Use this customized export option to get your data
                            with your applied filters and manage them easily.
                            Exporting data helps in creating backups.
                          </>
                        )}
                      </span>
                    </div>{" "}
                    <div className="report-inner-content mt-3">
                      <Row className="align-items-center">
                        <Col sm={12} md={4} lg={4}>
                          <div className="report-type">
                            <div
                              onClick={() =>
                                setSelectedReportType("QuickReport")
                              }
                              className={`report-type-option ${selectedReportType === "QuickReport" ? "active" : ""}`}
                            >
                              Quick report
                            </div>
                            <div
                              onClick={() =>
                                setSelectedReportType("Customized")
                              }
                              className={`report-type-option ${selectedReportType === "Customized" ? "active" : ""}`}
                            >
                              Customized
                            </div>
                          </div>
                        </Col>
                        <Col />
                      </Row>
                    </div>
                  </div>
                </Col>

                <Col sm={12} md={4} className="text-center">
                  <div
                    style={{
                      height: "220px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "0 auto",
                    }}
                  >
                    <img src={img} alt="" style={{ height: "220px" }} />
                  </div>
                </Col>
              </Row>

              {selectedReportType === "QuickReport" && (
                <div>
                  <div className="dotted-divider">
                    <span className="divider-text">
                      Just a single click is enough!
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    <Col sm={12} md={4} lg={3} style={{ marginTop: "16px" }}>
                      <h1
                        style={{
                          fontWeight: "500",
                          fontSize: "13px",
                          lineHeight: "17.55px",
                          color: "#212129",
                        }}
                      >
                        Type
                      </h1>
                      <CommonStaticDropdown
                        items={[
                          { value: "lead", label: "Lead" },
                          { value: "call", label: "Call" },
                          { value: "business", label: "Business" }
                        ]}
                        placeholder="Select"
                        value={quickReportType}
                        enableSearch={false}
                        onSelect={(item) => {
                          setQuickReportType(item);
                        }}
                      />
                    </Col>

                    <Col sm={12} md={4} lg={3} style={{ marginTop: "16px" }}>
                      <h1
                        style={{
                          fontWeight: "500",
                          fontSize: "13px",
                          lineHeight: "17.55px",
                          color: "#212129",
                        }}
                      >
                        Date Range
                      </h1>
                      <div
                        style={{ position: "relative", width: "100%" }}
                        className="react-datepicker-wrapper"
                      >
                        <DatePicker
                          selectsRange={true}
                          startDate={quick_from_date}
                          endDate={quick_to_date}
                          onChange={(update) => setQuickDateRange(update)}
                          className="react-datepicker__input-container textcolor"
                          style={{
                            width: "100%",
                          }}
                          placeholderText="Select"
                          wrapperClassName="w-100"
                        />
                        <FaRegCalendarAlt
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            color: "#8790A1",
                          }}
                        />
                      </div>
                    </Col>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    {quickReportList.map((item, index) => (
                      <div
                        onClick={() => !quickLoading && exportQuickGet(item)}
                        key={index}
                        style={{
                          cursor:
                            quickLoading && loadingItemId === item.value
                              ? "not-allowed"
                              : "pointer",
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderRadius: "16px",
                          padding: "12px",
                          border: "0.7px solid #F8F8F8",
                          backgroundColor: "#F8F8F8",
                          width: "calc(25% - 9px)",
                          boxSizing: "border-box",
                        }}
                      >
                        <div className="fs-14 fw-500 textcolor">
                          {item.label}
                        </div>
                        {quickLoading && loadingItemId === item.value && (
                          <Spinner
                            animation="border"
                            size="sm"
                            style={{ color: "#464FB1" }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedReportType === "Customized" && (
                <div>
                  <div className="dotted-divider">
                    <span className="divider-text">Customize your report</span>
                  </div>

                  <div className="mb-4">
                    <Row className="align-items-center">
                      <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }}>
                        <h1
                          style={{
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "17.55px",
                            color: "#212129",
                          }}
                        >
                          Type
                        </h1>
                        <CommonStaticDropdown
                          items={reportTypes}
                          placeholder="Select"
                          value={customReportType}
                          enableSearch={false}
                          onSelect={(item) => {
                            setCustomReportType(item);
                          }}
                        />
                      </Col>

                      {/* Dynamic Fields Rendering */}
                      {fieldsList.some(f => f.field === "fromDate" || f.field === "from_date") && (
                        <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }}>
                          <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                            Date Range
                          </h1>
                          <div style={{ position: "relative", width: "100%" }} className="react-datepicker-wrapper">
                            <DatePicker
                              selectsRange={true}
                              startDate={custom_from_date}
                              endDate={custom_to_date}
                              onChange={(update) => setCustomDateRange(update)}
                              className="react-datepicker__input-container textcolor"
                              style={{ width: "100%" }}
                              placeholderText="Select"
                              wrapperClassName="w-100"
                            />
                            <FaRegCalendarAlt
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                color: "#8790A1",
                              }}
                            />
                          </div>
                        </Col>
                      )}

                      {fieldsList.map((field) => {
                        const fieldName = field.field;

                        // Skip rendering fromDate/toDate/from_date/to_date separately as they are handled in the Date Range Picker above
                        if (fieldName === "fromDate" || fieldName === "toDate" || fieldName === "from_date" || fieldName === "to_date") {
                          return null;
                        }

                        // Sheets or multiselect options list
                        if (field.type === "multiselect" || fieldName === "sheets") {
                          return (
                            <Col sm={12} style={{ marginTop: "16px" }} key={fieldName}>
                              <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                                {field.label} {field.required && <span className="text-danger">*</span>}
                              </h1>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {field.options?.map((opt) => {
                                  const isSelected = filters[fieldName]?.includes(opt);
                                  return (
                                    <button
                                      key={opt}
                                      type="button"
                                      className="btn btn-sm"
                                      onClick={() => {
                                        const currentVal = filters[fieldName] || [];
                                        const newVal = currentVal.includes(opt)
                                          ? currentVal.filter(s => s !== opt)
                                          : [...currentVal, opt];
                                        setFilters(prev => ({ ...prev, [fieldName]: newVal }));
                                      }}
                                      style={{
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        backgroundColor: isSelected ? "#6929E2" : "transparent",
                                        color: isSelected ? "#FFFFFF" : "#212129",
                                        border: "1px solid",
                                        borderColor: isSelected ? "#6929E2" : "#E5E5E5",
                                      }}
                                    >
                                      {opt}
                                    </button>
                                  );
                                })}
                              </div>
                            </Col>
                          );
                        }

                        // Static select
                        if (field.type === "select") {
                          return (
                            <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }} key={fieldName}>
                              <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                                {field.label} {field.required && <span className="text-danger">*</span>}
                              </h1>
                              <CommonStaticDropdown
                                items={field.options?.map(opt => ({ value: opt, label: opt }))}
                                placeholder="Select"
                                value={filters[fieldName] ? { value: filters[fieldName], label: filters[fieldName] } : null}
                                enableSearch={false}
                                onSelect={(item) => {
                                  setFilters(prev => ({ ...prev, [fieldName]: item.value }));
                                }}
                              />
                            </Col>
                          );
                        }

                        // Async select or multiselect
                        if (field.type === "multiselect_async") {
                          if (fieldName !== "invoice_id") {
                            return (
                              <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }} key={fieldName}>
                                <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                                  {field.label} {field.required && <span className="text-danger">*</span>}
                                </h1>
                                <AsyncSelect
                                  isMulti
                                  cacheOptions
                                  defaultOptions
                                  value={filters[fieldName] || []}
                                  loadOptions={(inputValue) => loadAsyncOptions(inputValue, fieldName)}
                                  onChange={(selectedOptions) => {
                                    setFilters(prev => ({ ...prev, [fieldName]: selectedOptions || [] }));
                                  }}
                                  placeholder="Select"
                                  styles={multiselectCustomStyles}
                                  isClearable={true}
                                />
                              </Col>
                            );
                          }
                        }

                        if (field.type === "select_async") {
                          if (fieldName !== "invoice_id") {
                            return (
                              <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }} key={fieldName}>
                                <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                                  {field.label} {field.required && <span className="text-danger">*</span>}
                                </h1>
                                <CommonDropdown
                                  apiUrl={getAsyncUrl(fieldName)}
                                  placeholder="Select"
                                  value={filters[fieldName] || null}
                                  onSelect={(item) => {
                                    setFilters(prev => ({ ...prev, [fieldName]: item }));
                                  }}
                                />
                              </Col>
                            );
                          }
                        }

                        // Text input or default fallback (e.g. invoice_id)
                        return (
                          <Col sm={12} md={6} lg={4} style={{ marginTop: "16px" }} key={fieldName}>
                            <h1 style={{ fontWeight: "500", fontSize: "13px", lineHeight: "17.55px", color: "#212129" }}>
                              {field.label} {field.required && <span className="text-danger">*</span>}
                            </h1>
                            <input
                              type="text"
                              className="form-control textcolor"
                              value={filters[fieldName] || ""}
                              onChange={(e) => {
                                setFilters(prev => ({ ...prev, [fieldName]: e.target.value }));
                              }}
                              placeholder={fieldName === "invoice_id" ? "e.g. 1, 2, 3" : `Enter ${field.label}`}
                              style={{
                                height: "38px",
                                borderRadius: "8px",
                                border: "1px solid #E5E5E5",
                                padding: "8px 12px",
                                fontSize: "13px",
                              }}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </div>

                  <Col
                    sm={12}
                    lg={12}
                    className="d-flex justify-content-end align-items-center pt-5"
                  >
                    <button
                      style={{
                        width: "126px",
                        height: "44px",
                        borderRadius: "8px",
                        border: "0.6px solid #E5E5E5",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={handleReset}
                    >
                      <p style={{ margin: 0 }}>Reset</p>
                    </button>

                    <button
                      style={{
                        width: "126px",
                        height: "44px",
                        borderRadius: "8px",
                        color: "#FFFFFF",
                        border: "0.6px solid #E5E5E5",
                        backgroundColor: "#6929E2",
                        marginLeft: "8px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      disabled={loading}
                      onClick={() => handleCustomExport()}
                    >
                      {loading ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ color: "#FFFFFF" }}
                        />
                      ) : (
                        <p style={{ margin: 0 }}>Export</p>
                      )}
                    </button>
                  </Col>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default ReportPage;
